import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform, useInView } from "motion/react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  Users,
  ArrowRight,
  Building2,
  TrendingUp,
  Shield,
  Zap,
  Target,
  Upload,
  Eye,
  Check,
  AlertTriangle,
  Star,
  Settings,
  Smartphone,
  Edit3,
  Download,
  Brain,
  MessageSquare,
  Sparkles,
  FileSearch,
  Bot,
  Cpu,
  Network,
  Scan,
  PenTool,
  Send,
  Lock,
  Share2,
  Mail,
  Link,
  Save,
  Activity,
  BarChart3,
  MessageCircle,
  Wand2,
  ShieldCheck,
} from "lucide-react";

const CounterAnimation = ({ value, duration = 2000 }: { value: number; duration?: number }) => {
  const [count, setCount] = useState(0);
  const ref = React.useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let startTime: number;
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * value));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, value, duration]);

  return <span ref={ref}>{count}</span>;
};

const AIDocumentMockup = () => {
  const [currentMessage, setCurrentMessage] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const messages = [
    { type: "user", text: "What are the key terms in this purchase agreement?" },
    { type: "ai", text: "I've analyzed the purchase agreement. Here are the key terms:" },
    {
      type: "ai-analysis",
      data: {
        price: "$1,250,000",
        closing: "December 15, 2024",
        contingencies: "Inspection (10 days), Financing (21 days)",
        buyer: "Johnson Family Trust",
        earnest: "$25,000",
      },
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      if (currentMessage < messages.length - 1) {
        setCurrentMessage((prev) => prev + 1);
        if (currentMessage === 0) {
          setIsAnalyzing(true);
          setTimeout(() => setIsAnalyzing(false), 1500);
        }
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [currentMessage]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden max-w-4xl mx-auto"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <Brain className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-medium">SmartRead AI</h3>
              <p className="text-sm text-purple-100">Document Intelligence Assistant</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm">Active</span>
          </div>
        </div>
      </div>

      {/* Document Preview */}
      <div className="flex">
        {/* Document Side */}
        <div className="w-1/2 bg-gray-50 border-r border-gray-200 p-6">
          <div className="mb-4 flex items-center gap-3">
            <FileText className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-gray-900">Purchase_Agreement.pdf</span>
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200 h-80 overflow-hidden relative">
            {/* Document Content Simulation */}
            <div className="text-xs text-gray-600 space-y-2">
              <div className="font-bold text-center mb-4">REAL ESTATE PURCHASE AGREEMENT</div>
              <div className="h-2 bg-gray-200 rounded w-full"></div>
              <div className="h-2 bg-gray-200 rounded w-3/4"></div>
              <div className="h-2 bg-gray-200 rounded w-full"></div>
              <div className="h-2 bg-yellow-200 rounded w-2/3"></div>
              <div className="h-2 bg-gray-200 rounded w-full"></div>
              <div className="h-2 bg-blue-200 rounded w-1/2"></div>
              <div className="h-2 bg-gray-200 rounded w-full"></div>
              <div className="h-2 bg-green-200 rounded w-3/4"></div>
              <div className="h-2 bg-gray-200 rounded w-full"></div>
              <div className="h-2 bg-red-200 rounded w-2/3"></div>
            </div>

            {/* Scanning Animation */}
            {isAnalyzing && (
              <motion.div
                className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent"
                animate={{ y: [0, 320, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              />
            )}
          </div>
        </div>

        {/* Chat Side */}
        <div className="w-1/2 flex flex-col h-96">
          <div className="flex-1 p-6 space-y-4 overflow-hidden">
            {messages.slice(0, currentMessage + 1).map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.5 }}
                className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}
              >
                {message.type === "user" ? (
                  <div className="bg-blue-600 text-white p-3 rounded-2xl rounded-br-md max-w-xs">
                    <p className="text-sm">{message.text}</p>
                  </div>
                ) : message.type === "ai-analysis" ? (
                  <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-4 rounded-2xl border-2 border-purple-200 max-w-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium text-purple-900">Key Analysis</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Purchase Price:</span>
                        <span className="font-medium text-green-600">{message.data?.price}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Closing Date:</span>
                        <span className="font-medium">{message.data?.closing}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Buyer:</span>
                        <span className="font-medium">{message.data?.buyer}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Earnest Money:</span>
                        <span className="font-medium">{message.data?.earnest}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-100 p-3 rounded-2xl rounded-bl-md max-w-xs">
                    <div className="flex items-center gap-2 mb-1">
                      <Brain className="w-4 h-4 text-purple-600" />
                      <span className="text-xs font-medium text-purple-900">SmartRead AI</span>
                    </div>
                    <p className="text-sm">{message.text}</p>
                  </div>
                )}
              </motion.div>
            ))}

            {isAnalyzing && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-start"
              >
                <div className="bg-gray-100 p-3 rounded-2xl rounded-bl-md">
                  <div className="flex items-center gap-2">
                    <div className="flex space-x-1">
                      <div
                        className="w-2 h-2 bg-purple-600 rounded-full animate-bounce"
                        style={{ animationDelay: "0ms" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-purple-600 rounded-full animate-bounce"
                        style={{ animationDelay: "150ms" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-purple-600 rounded-full animate-bounce"
                        style={{ animationDelay: "300ms" }}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-500">Analyzing document...</span>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Chat Input */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <MessageSquare className="w-5 h-5 text-gray-400" />
              <div className="flex-1 text-sm text-gray-500">
                Ask me anything about this document...
              </div>
              <Send className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const FeatureSection = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8 }}
      className={`py-16 md:py-24 ${className}`}
    >
      {children}
    </motion.section>
  );
};

export default function SmartReadAILandingPage() {
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

  const coreFeatures = [
    {
      title: "AI-Augmented Document Chat",
      description:
        "Upload contracts, disclosures, inspection reports, or agreements directly from RealEsy WorkDrive or your computer. Instantly launch an AI chat session where you can ask any question about the document in natural language. The AI responds with clear, actionable insights, saving you hours of manual reading and interpretation.",
      benefits: [
        "Natural language chat interface with any document type",
        "Instant AI responses with contextual understanding",
        "Direct integration with RealEsy WorkDrive file system",
        "Support for contracts, disclosures, inspection reports, and agreements",
        "Clear, actionable insights that save hours of manual review",
      ],
      icon: MessageSquare,
      color: "text-blue-600",
    },
    {
      title: "Automatic Extraction of Key Facts & Dates",
      description:
        "SmartRead scans documents and automatically highlights critical details like closing dates, contingencies, obligations, renewal clauses, and payment terms. No more missed fine print or overlooked dates — everything is pulled into an easy-to-review summary so you and your clients always stay ahead.",
      benefits: [
        "Automatic extraction of closing dates and critical deadlines",
        "Identification of contingencies and obligations",
        "Renewal clauses and payment terms highlighting",
        "Fine print analysis to prevent oversight",
        "Easy-to-review summary format for quick understanding",
      ],
      icon: Scan,
      color: "text-green-600",
    },
    {
      title: "Save and Continue Conversations",
      description:
        "Every AI chat can be saved so agents or managers can return later without starting over. This creates a document history that can be referenced during compliance checks, client follow-ups, or internal reviews. For brokerages, it also ensures continuity across teams when multiple people are reviewing the same files.",
      benefits: [
        "Persistent conversation history for all document analyses",
        "Easy reference during compliance checks and reviews",
        "Team continuity when multiple people review documents",
        "Historical context for client follow-ups and meetings",
        "Complete audit trail of all AI interactions",
      ],
      icon: Save,
      color: "text-indigo-600",
    },
    {
      title: "Built-In Editing and Document Preparation",
      description:
        "Use the integrated editor to polish AI-generated summaries or notes. Adjust the tone, add branding, or reformat for clients. Once complete, export to PDF or copy directly into an email. This built-in preparation flow eliminates the need for external tools, keeping all work inside RealEsy.",
      benefits: [
        "Integrated editor for polishing AI-generated content",
        "Tone adjustment and branding customization options",
        "Client-ready formatting with professional templates",
        "Direct export to PDF or email integration",
        "All-in-one workflow without external tool dependencies",
      ],
      icon: PenTool,
      color: "text-teal-600",
    },
    {
      title: "One-Click Export & Secure Email Sharing",
      description:
        "When a summary or report is ready, send it instantly using RealEsy's built-in email feature. Add a password or expiry date for sensitive documents. Whether you're updating a seller, briefing a buyer, or sharing with another agent, secure distribution is built right in.",
      benefits: [
        "One-click export and email distribution system",
        "Password protection for sensitive document summaries",
        "Expiry date settings for time-sensitive information",
        "Built-in secure sharing without third-party tools",
        "Professional templates for client communication",
      ],
      icon: Mail,
      color: "text-red-600",
    },
    {
      title: "Pre-Built & Custom Prompts for Real Estate",
      description:
        "SmartRead comes with a library of pre-built prompts designed for real estate workflows — like identifying key contingencies in a purchase agreement or summarizing a lease renewal. Agents can also create custom prompts tailored to their specific markets and save them for future use, ensuring faster and more accurate results.",
      benefits: [
        "Pre-built prompt library for common real estate scenarios",
        "Custom prompt creation for specific market needs",
        "Saved prompts for consistent and faster analysis",
        "Market-specific customization capabilities",
        "Standardized analysis across team members",
      ],
      icon: Wand2,
      color: "text-amber-600",
    },
    {
      title: "Favorites for Speed & Consistency",
      description:
        "Mark your most useful prompts and document types as favorites. This creates a personalized toolbox of AI-powered shortcuts so you can get repeatable, high-quality results in seconds, without retyping queries.",
      benefits: [
        "Personalized favorites system for frequent prompts",
        "AI-powered shortcuts for common document types",
        "Repeatable, high-quality analysis results",
        "Time-saving interface with one-click access",
        "Customizable toolbox for individual workflow needs",
      ],
      icon: Star,
      color: "text-yellow-600",
    },
    {
      title: "Advanced Intelligence Mode",
      description:
        "When you need to go deeper, activate Advanced Intelligence Mode. This setting digs into contracts, financial documents, or inspection reports to pull out hidden risks, unusual clauses, or numerical patterns that might otherwise be overlooked. From legal fine print to budget breakdowns, SmartRead delivers insights far beyond simple summaries.",
      benefits: [
        "Deep analysis mode for complex document examination",
        "Hidden risk identification and unusual clause detection",
        "Numerical pattern analysis for financial documents",
        "Legal fine print examination and interpretation",
        "Advanced insights beyond standard document summaries",
      ],
      icon: Cpu,
      color: "text-violet-600",
    },
  ];

  const faqs = [
    {
      q: "How accurate is SmartRead AI in analyzing real estate documents?",
      a: "SmartRead AI is specifically trained on real estate documents and legal language, providing highly accurate analysis of contracts, disclosures, and agreements. However, it's designed to assist professionals, not replace legal review.",
    },
    {
      q: "Can SmartRead AI handle different document formats?",
      a: "Yes, SmartRead AI supports PDFs, Word documents, images, and scanned documents. It can extract text and analyze content from virtually any document format used in real estate.",
    },
    {
      q: "Is my document data secure with SmartRead AI?",
      a: "Absolutely. All documents are encrypted in transit and at rest. SmartRead AI processes documents securely and doesn't store personal information beyond your session unless you choose to save conversations.",
    },
    {
      q: "Can I customize the AI prompts for my specific needs?",
      a: "Yes, you can create custom prompts tailored to your market, property types, or specific analysis needs. These can be saved and reused for consistent results across your team.",
    },
    {
      q: "Can multiple team members access the same AI conversation?",
      a: "Yes, saved conversations can be shared with team members, providing continuity when multiple people need to review the same document analysis.",
    },
    {
      q: "What languages does SmartRead AI support?",
      a: "SmartRead AI primarily supports English for real estate documents, with additional language support being added based on market demand.",
    },
    {
      q: "How fast does SmartRead AI analyze documents?",
      a: "Most document analysis is completed within seconds to a few minutes, depending on the document length and complexity. Large documents may take slightly longer for comprehensive analysis.",
    },
  ];

  return (
    <>
      {/* Enhanced Hero Section with AI Effects */}
      <section className="relative bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 py-20 md:py-32 overflow-hidden">
        {/* Animated Neural Network Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => {
            const randomX1 = Math.random() * 100;
            const randomY1 = Math.random() * 100;
            const randomX2 = Math.random() * 100;
            const randomY2 = Math.random() * 100;
            const randomDuration = Math.random() * 20 + 15;

            return (
              <motion.div
                key={`neural-${i}`}
                initial={{
                  opacity: 0,
                  left: `${randomX1}%`,
                  top: `${randomY1}%`,
                }}
                animate={{
                  opacity: [0, 0.3, 0.1, 0.4, 0],
                  scale: [0.5, 1, 0.8, 1.2, 0.5],
                  left: `${randomX2}%`,
                  top: `${randomY2}%`,
                }}
                transition={{
                  duration: randomDuration,
                  repeat: Infinity,
                  delay: i * 0.5,
                  ease: "easeInOut",
                }}
                className="absolute w-2 h-2 bg-purple-400 rounded-full"
              />
            );
          })}
          {/* Connection Lines */}
          <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 1 }}>
            {[...Array(8)].map((_, i) => (
              <motion.line
                key={`line-${i}`}
                x1={Math.random() * 100 + "%"}
                y1={Math.random() * 100 + "%"}
                x2={Math.random() * 100 + "%"}
                y2={Math.random() * 100 + "%"}
                stroke="url(#gradient)"
                strokeWidth="1"
                strokeDasharray="3,3"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{
                  pathLength: [0, 1, 0],
                  opacity: [0, 0.3, 0],
                }}
                transition={{
                  duration: Math.random() * 10 + 8,
                  repeat: Infinity,
                  delay: i * 1.5,
                  ease: "easeInOut",
                }}
              />
            ))}
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.3" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        {/* Floating AI Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(12)].map((_, i) => {
            const randomStartX = Math.random() * 100;
            const randomEndX = Math.random() * 100;
            const randomDuration = Math.random() * 25 + 20;
            const randomDelay = i * 2;

            return (
              <motion.div
                key={`ai-${i}`}
                initial={{
                  opacity: 0,
                  left: `${randomStartX}%`,
                  top: "110%",
                }}
                animate={{
                  opacity: [0, 0.6, 0.4, 0.8, 0],
                  left: `${randomEndX}%`,
                  top: "-10%",
                  rotate: [0, 360],
                }}
                transition={{
                  duration: randomDuration,
                  repeat: Infinity,
                  delay: randomDelay,
                  ease: "linear",
                }}
                className="absolute"
              >
                {i % 4 === 0 ? (
                  <Brain className="w-8 h-8 text-purple-400" />
                ) : i % 4 === 1 ? (
                  <Sparkles className="w-6 h-6 text-blue-400" />
                ) : i % 4 === 2 ? (
                  <Bot className="w-7 h-7 text-indigo-400" />
                ) : (
                  <MessageSquare className="w-6 h-6 text-purple-400" />
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Pulsating AI Brain */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/4 left-1/3 w-96 h-96 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1.2, 1, 1.2],
              opacity: [0.1, 0.3, 0.1],
            }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute bottom-1/4 right-1/3 w-80 h-80 bg-gradient-to-l from-indigo-400 to-purple-400 rounded-full blur-3xl"
          />
        </div>

        <div className="container mx-auto px-6 relative">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="max-w-6xl mx-auto space-y-8">
                <div className="space-y-6">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="flex justify-center mb-6"
                  >
                    <div className="flex items-center gap-4 bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 border border-purple-200">
                      <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-slate-700 font-medium">Powered by Advanced AI</span>
                    </div>
                  </motion.div>

                  <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-blue-800 leading-tight">
                    RealEsy SmartRead AI — Document Intelligence for Real Estate
                  </h1>
                </div>

                <div className="max-w-4xl mx-auto space-y-6">
                  <p className="text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                    Your AI assistant that reads, extracts, and summarizes documents for brokerages
                    and agents.
                  </p>
                  <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                    RealEsy SmartRead AI is built to save you time, reduce mistakes, and simplify
                    every contract, disclosure, or agreement your real estate business depends on.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center pt-8">
                  <Button
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] transition-all duration-300 w-full sm:w-auto border-0"
                  >
                    <Brain className="mr-3 w-5 h-5" />
                    Try SmartRead AI
                    <ArrowRight className="ml-3 w-5 h-5" />
                  </Button>
                </div>
              </div>
            </motion.div>

            <div className="max-w-6xl mx-auto">
              <AIDocumentMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Why Agents Love SmartRead AI Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left side: AI Chat Interface Mockup */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:pr-8"
              >
                <div className="relative bg-white rounded-2xl shadow-xl p-8 border border-gray-200 overflow-hidden">
                  {/* AI Brain Animation */}
                  <div className="absolute top-4 right-4 w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                    >
                      <Brain className="w-6 h-6 text-white" />
                    </motion.div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h4 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-3">
                        <Bot className="w-6 h-6 text-purple-600" />
                        Your AI Assistant That Reads for You
                      </h4>
                    </div>

                    <div className="space-y-4">
                      {/* Chat Messages */}
                      <div className="bg-blue-50 p-4 rounded-2xl rounded-tr-md">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                            <Users className="w-3 h-3 text-white" />
                          </div>
                          <span className="text-sm font-medium text-blue-900">Agent Question</span>
                        </div>
                        <p className="text-sm">
                          "What are the inspection contingencies in this contract?"
                        </p>
                      </div>

                      <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-2xl rounded-tl-md border-2 border-purple-200">
                        <div className="flex items-center gap-2 mb-3">
                          <motion.div
                            animate={{ scale: [1, 1.1, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          >
                            <Sparkles className="w-5 h-5 text-purple-600" />
                          </motion.div>
                          <span className="text-sm font-medium text-purple-900">SmartRead AI</span>
                        </div>
                        <div className="space-y-3">
                          <p className="text-sm font-medium">I found 3 inspection contingencies:</p>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-xs">
                              <Check className="w-3 h-3 text-green-600" />
                              <span>
                                <strong>General Inspection:</strong> 10 days from acceptance
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                              <Check className="w-3 h-3 text-green-600" />
                              <span>
                                <strong>Pest Inspection:</strong> 7 days from acceptance
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                              <Check className="w-3 h-3 text-green-600" />
                              <span>
                                <strong>Roof Inspection:</strong> 14 days from acceptance
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-yellow-50 p-3 rounded-2xl border-2 border-yellow-200">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle className="w-4 h-4 text-yellow-600" />
                          <span className="text-sm font-medium text-yellow-900">Key Reminder</span>
                        </div>
                        <p className="text-xs text-yellow-800">
                          Inspection deadlines start from contract acceptance date.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Right side: Content */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-8"
              >
                <div>
                  <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                    Why Agents Love SmartRead AI
                  </h2>
                  <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-8">
                    Transform how you read and understand real estate documents with AI that works
                    like your personal assistant.
                  </p>
                </div>

                <div className="space-y-8">
                  {[
                    {
                      icon: MessageSquare,
                      title: "Your AI Assistant That Reads for You",
                      description:
                        "Instantly summarize lengthy real estate contracts, disclosures, and agreements. Extract key dates, obligations, and next steps — no more missed details. Ask your document anything in plain language — SmartRead answers clearly.",
                    },
                    {
                      icon: Zap,
                      title: "Work Smarter, Not Slower",
                      description:
                        "Prepare faster for showings, listings, and closings. Convert complex legal or financial docs into client-ready summaries in seconds. Save and reuse your favorite prompts or create custom ones.",
                    },
                    {
                      icon: Target,
                      title: "Impress Your Clients, Instantly",
                      description:
                        "Generate follow-up summaries to share with buyers or sellers. Use the built-in editor + email feature to turn AI insights into polished documents. Look professional, precise, and proactive — even under pressure.",
                    },
                  ].map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 + index * 0.1 }}
                      className="bg-gray-50 p-6 rounded-2xl border border-gray-200"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <benefit.icon className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 mb-3">{benefit.title}</h4>
                          <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Why Brokerages Trust SmartRead AI Section */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left side: Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-8"
              >
                <div>
                  <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                    Why Brokerages Trust SmartRead AI
                  </h2>
                  <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-8">
                    Enterprise-grade AI that scales across your entire team while maintaining
                    compliance and consistency.
                  </p>
                </div>

                <div className="space-y-8">
                  {[
                    {
                      icon: Network,
                      title: "Your Team's AI-Powered Knowledge Engine",
                      description:
                        "Centralize document reviews with shared AI chats from RealEsy WorkDrive. Help every agent — even new hires — understand and act on documents faster. Reduce training time: let RealEsy AI teach your contracts with smart prompts.",
                    },
                    {
                      icon: ShieldCheck,
                      title: "Compliance, Clarity, Control",
                      description:
                        "Never miss a deadline, clause, or legal nuance — SmartRead flags risks and key terms automatically. Compare versions to detect changes that matter. Keep a recorded memory of every analysis and conversation for audit trails.",
                    },
                    {
                      icon: TrendingUp,
                      title: "Time is Money — Save Both",
                      description:
                        "Agents spend hours reading and re-reading contracts — SmartRead automates it. Better-prepared agents = faster closings, fewer mistakes, happier clients.",
                    },
                  ].map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 + index * 0.1 }}
                      className="bg-white p-6 rounded-2xl border border-gray-200"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <benefit.icon className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 mb-3">{benefit.title}</h4>
                          <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Right side: Brokerage AI Dashboard Mockup */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="lg:pl-8"
              >
                <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                  {/* Dashboard Header */}
                  <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-lg">Brokerage AI Dashboard</h3>
                        <p className="text-purple-100 text-sm">Team Document Intelligence</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Activity className="w-5 h-5" />
                        <span className="text-sm">Live</span>
                      </div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="p-6 grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-xl">
                      <div className="flex items-center gap-3">
                        <FileSearch className="w-6 h-6 text-blue-600" />
                        <div>
                          <div className="text-2xl font-bold text-blue-600">
                            <CounterAnimation value={1247} />
                          </div>
                          <div className="text-sm text-blue-800">Documents Analyzed</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-xl">
                      <div className="flex items-center gap-3">
                        <Clock className="w-6 h-6 text-green-600" />
                        <div>
                          <div className="text-2xl font-bold text-green-600">
                            <CounterAnimation value={342} />h
                          </div>
                          <div className="text-sm text-green-800">Time Saved</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Team Activity */}
                  <div className="px-6 pb-6">
                    <h4 className="font-bold text-gray-900 mb-4">Recent Team Activity</h4>
                    <div className="space-y-3">
                      {[
                        {
                          agent: "Sarah J.",
                          action: "Analyzed purchase agreement",
                          time: "2 min ago",
                          status: "completed",
                        },
                        {
                          agent: "Mike C.",
                          action: "Created custom prompt",
                          time: "5 min ago",
                          status: "saved",
                        },
                        {
                          agent: "Lisa R.",
                          action: "Shared AI summary with client",
                          time: "8 min ago",
                          status: "sent",
                        },
                      ].map((activity, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.5 + index * 0.1 }}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-xs font-medium text-purple-600">
                              {activity.agent.split(" ")[0][0]}
                              {activity.agent.split(" ")[1][0]}
                            </span>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                            <p className="text-xs text-gray-600">{activity.time}</p>
                          </div>
                          <Badge
                            className={`text-xs ${activity.status === "completed"
                              ? "bg-green-100 text-green-800"
                              : activity.status === "saved"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-purple-100 text-purple-800"
                              }`}
                          >
                            {activity.status}
                          </Badge>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Core Features Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-blue-600 mb-6">
                Expanded Core Features of RealEsy SmartRead AI
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                Comprehensive AI-powered document intelligence designed specifically for real estate
                professionals.
              </p>
            </div>

            <div className="space-y-24">
              {coreFeatures.map((feature, index) => (
                <div
                  key={index}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? "lg:grid-flow-col-dense" : ""}`}
                >
                  {/* Text Content */}
                  <motion.div
                    initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className={`space-y-6 ${index % 2 === 1 ? "lg:col-start-2" : ""}`}
                  >
                    <div>
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                        {feature.title}
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed mb-6">
                        {feature.description}
                      </p>
                    </div>

                    <div className="space-y-4">
                      {feature.benefits.map((benefit, benefitIndex) => (
                        <motion.div
                          key={benefitIndex}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.1 + benefitIndex * 0.1 }}
                          className="flex items-start gap-3"
                        >
                          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-4 h-4 text-green-600" />
                          </div>
                          <span className="text-gray-700">{benefit}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>

                  {/* AI Feature Illustrations */}
                  <motion.div
                    initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className={`${index % 2 === 1 ? "lg:col-start-1 lg:row-start-1" : ""}`}
                  >
                    {/* Dynamic AI feature-specific illustrations */}
                    {index === 0 && (
                      // AI Chat Interface
                      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                        <div className="space-y-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                              <MessageSquare className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-bold text-lg">AI Document Chat</h4>
                              <p className="text-sm text-gray-600">Natural language interface</p>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div className="bg-blue-50 p-4 rounded-2xl rounded-tr-md">
                              <p className="text-sm">"What's the earnest money deposit amount?"</p>
                            </div>
                            <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-2xl rounded-tl-md">
                              <div className="flex items-center gap-2 mb-2">
                                <Brain className="w-4 h-4 text-purple-600" />
                                <span className="text-xs font-medium">SmartRead AI</span>
                              </div>
                              <p className="text-sm">
                                The earnest money deposit is <strong>$25,000</strong>, due within 3
                                business days of acceptance.
                              </p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-xl">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <FileText className="w-4 h-4" />
                                <span>Source: Purchase Agreement, Section 3.2</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {index === 1 && (
                      // Automatic Extraction
                      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                        <div className="space-y-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-gradient-to-r from-green-600 to-teal-600 rounded-full flex items-center justify-center">
                              <Scan className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-bold text-lg">Auto-Extracted Key Data</h4>
                              <p className="text-sm text-gray-600">
                                Critical information identified
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 gap-4">
                            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                              <div className="flex items-center gap-2 mb-2">
                                <Calendar className="w-4 h-4 text-red-600" />
                                <span className="font-medium text-red-800">Critical Dates</span>
                              </div>
                              <div className="space-y-1 text-sm">
                                <div>
                                  Closing: <strong>Dec 15, 2024</strong>
                                </div>
                                <div>
                                  Inspection: <strong>Dec 1, 2024</strong>
                                </div>
                                <div>
                                  Loan Approval: <strong>Dec 8, 2024</strong>
                                </div>
                              </div>
                            </div>

                            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                              <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle className="w-4 h-4 text-yellow-600" />
                                <span className="font-medium text-yellow-800">Contingencies</span>
                              </div>
                              <div className="space-y-1 text-sm">
                                <div>• Inspection (10 days)</div>
                                <div>• Financing (21 days)</div>
                                <div>• Appraisal (14 days)</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {index === 2 && (
                      // Save Conversations
                      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                        <div className="space-y-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full flex items-center justify-center">
                              <Save className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-bold text-lg">Conversation History</h4>
                              <p className="text-sm text-gray-600">Persistent chat sessions</p>
                            </div>
                          </div>

                          <div className="space-y-3">
                            {[
                              {
                                doc: "Purchase Agreement - Johnson",
                                date: "2 hours ago",
                                queries: 8,
                              },
                              {
                                doc: "Disclosure Statement - Smith",
                                date: "1 day ago",
                                queries: 12,
                              },
                              { doc: "Lease Agreement - Brown", date: "2 days ago", queries: 6 },
                            ].map((conversation, idx) => (
                              <div key={idx} className="bg-gray-50 p-4 rounded-lg border">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium text-gray-900">{conversation.doc}</p>
                                    <p className="text-xs text-gray-600">
                                      {conversation.queries} questions asked
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs text-gray-500">{conversation.date}</p>
                                    <Button size="sm" variant="outline" className="mt-1">
                                      <MessageCircle className="w-3 h-3 mr-1" />
                                      Resume
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {index === 3 && (
                      // Built-in Editor
                      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                        <div className="space-y-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-gradient-to-r from-teal-600 to-blue-600 rounded-full flex items-center justify-center">
                              <PenTool className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-bold text-lg">Built-in Editor</h4>
                              <p className="text-sm text-gray-600">Professional text editing</p>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                              <div className="flex items-center gap-2 mb-3">
                                <Wand2 className="w-4 h-4 text-teal-600" />
                                <span className="text-sm font-medium">AI-Generated Summary</span>
                              </div>
                              <div className="bg-gray-50 p-3 rounded text-sm">
                                The contract includes standard terms with inspection
                                contingencies...
                              </div>
                            </div>

                            <div className="flex justify-center">
                              <ArrowRight className="w-5 h-5 text-teal-600" />
                            </div>

                            {/* CKEditor Interface */}
                            <div className="border-2 border-teal-200 rounded-lg bg-white overflow-hidden">
                              {/* CKEditor Toolbar */}
                              <div className="bg-gray-50 border-b border-gray-200 p-2">
                                <div className="flex items-center gap-1">
                                  <div className="flex items-center gap-1 border-r border-gray-300 pr-2">
                                    <div className="w-6 h-6 bg-gray-200 rounded border flex items-center justify-center text-xs font-bold">
                                      B
                                    </div>
                                    <div className="w-6 h-6 bg-gray-200 rounded border flex items-center justify-center text-xs italic">
                                      I
                                    </div>
                                    <div className="w-6 h-6 bg-gray-200 rounded border flex items-center justify-center text-xs underline">
                                      U
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 border-r border-gray-300 pr-2">
                                    <div className="w-6 h-6 bg-gray-200 rounded border flex items-center justify-center">
                                      <div className="w-3 h-2 bg-gray-500 rounded-sm"></div>
                                    </div>
                                    <div className="w-6 h-6 bg-gray-200 rounded border flex items-center justify-center">
                                      <div className="w-2 h-3 bg-gray-500 rounded-sm"></div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 border-r border-gray-300 pr-2">
                                    <div className="w-8 h-6 bg-gray-200 rounded border text-xs flex items-center justify-center">
                                      H1
                                    </div>
                                    <div className="w-8 h-6 bg-gray-200 rounded border text-xs flex items-center justify-center">
                                      H2
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <div className="w-6 h-6 bg-blue-100 rounded border flex items-center justify-center">
                                      <Link className="w-3 h-3 text-blue-600" />
                                    </div>
                                    <div className="w-6 h-6 bg-green-100 rounded border flex items-center justify-center">
                                      <Check className="w-3 h-3 text-green-600" />
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Editor Content Area */}
                              <div className="p-4 bg-white min-h-[120px]">
                                <div className="text-sm space-y-3">
                                  <div className="font-bold text-teal-800">
                                    Your Johnson Family Trust Offer Summary
                                  </div>
                                  <div className="text-gray-800 leading-relaxed">
                                    <span className="bg-yellow-100 px-1 rounded">Good news!</span>{" "}
                                    Your offer has been <strong>accepted</strong>.
                                  </div>
                                  <div className="text-gray-700">
                                    <strong className="text-teal-700">Key next steps:</strong>
                                    <ul className="list-disc ml-4 mt-1 space-y-1">
                                      <li>
                                        🏠 Home inspection by <strong>Dec 1st</strong>
                                      </li>
                                      <li>
                                        💰 Secure financing by <strong>Dec 8th</strong>
                                      </li>
                                      <li>
                                        🔑 Close on <strong>Dec 15th</strong>
                                      </li>
                                    </ul>
                                  </div>
                                </div>

                                {/* Cursor indicator */}
                                <div className="w-px h-4 bg-gray-800 animate-pulse mt-2"></div>
                              </div>

                              {/* CKEditor Footer */}
                              <div className="bg-gray-50 border-t border-gray-200 px-4 py-2 flex items-center justify-between text-xs text-gray-600">
                                <div className="flex items-center gap-2">
                                  <Edit3 className="w-3 h-3" />
                                  <span>CKEditor 5</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span>Words: 47</span>
                                  <span>•</span>
                                  <span>Characters: 284</span>
                                </div>
                              </div>
                            </div>

                            <div className="flex gap-2">
                              <Button size="sm" className="bg-teal-600">
                                <Mail className="w-3 h-3 mr-1" />
                                Email
                              </Button>
                              <Button size="sm" variant="outline">
                                <Download className="w-3 h-3 mr-1" />
                                PDF
                              </Button>
                              <Button size="sm" variant="outline" className="ml-auto">
                                <Save className="w-3 h-3 mr-1" />
                                Save Draft
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {index === 4 && (
                      // Email Sharing
                      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                        <div className="space-y-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-gradient-to-r from-red-600 to-pink-600 rounded-full flex items-center justify-center">
                              <Mail className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-bold text-lg">Secure Email Sharing</h4>
                              <p className="text-sm text-gray-600">One-click distribution</p>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                              <div className="flex items-center justify-between mb-3">
                                <span className="font-medium text-red-800">Email Settings</span>
                                <Lock className="w-4 h-4 text-red-600" />
                              </div>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span>Password Protected:</span>
                                  <span className="text-green-600">✓ Enabled</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Expires:</span>
                                  <span>7 days</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Recipients:</span>
                                  <span>sarah@johnsonfamily.com</span>
                                </div>
                              </div>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="text-sm text-gray-700">
                                <strong>Subject:</strong> Contract Summary - 123 Sunset Blvd
                                <br />
                                <strong>Preview:</strong> Here's your personalized summary of the
                                purchase agreement terms...
                              </div>
                            </div>

                            <Button className="w-full bg-red-600 hover:bg-red-700">
                              <Send className="w-4 h-4 mr-2" />
                              Send Secure Email
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {index === 5 && (
                      // Custom Prompts
                      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                        <div className="space-y-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center">
                              <Wand2 className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-bold text-lg">Smart Prompts Library</h4>
                              <p className="text-sm text-gray-600">Pre-built & custom</p>
                            </div>
                          </div>

                          <div className="space-y-3">
                            <div className="text-sm font-medium text-gray-700 mb-2">
                              Popular Pre-Built Prompts:
                            </div>
                            {[
                              {
                                name: "Extract Contingencies",
                                usage: "1.2k uses",
                                category: "Purchase Agreement",
                              },
                              {
                                name: "Summarize Key Terms",
                                usage: "890 uses",
                                category: "General",
                              },
                              {
                                name: "Find Important Dates",
                                usage: "756 uses",
                                category: "Deadlines",
                              },
                            ].map((prompt, idx) => (
                              <div
                                key={idx}
                                className="bg-amber-50 p-3 rounded-lg border border-amber-200"
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium text-amber-900">{prompt.name}</p>
                                    <p className="text-xs text-amber-700">
                                      {prompt.category} • {prompt.usage}
                                    </p>
                                  </div>
                                  <Button size="sm" variant="outline">
                                    Use
                                  </Button>
                                </div>
                              </div>
                            ))}

                            <div className="border-t pt-3 mt-4">
                              <div className="text-sm font-medium text-gray-700 mb-2">
                                Your Custom Prompts:
                              </div>
                              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium text-blue-900">
                                      "HOA Requirements Check"
                                    </p>
                                    <p className="text-xs text-blue-700">
                                      Created by you • 23 uses
                                    </p>
                                  </div>
                                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {index === 6 && (
                      // Favorites System
                      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                        <div className="space-y-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-gradient-to-r from-yellow-600 to-amber-600 rounded-full flex items-center justify-center">
                              <Star className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-bold text-lg">Your AI Favorites</h4>
                              <p className="text-sm text-gray-600">Quick access shortcuts</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            {[
                              {
                                name: "Purchase Summary",
                                icon: FileText,
                                color: "bg-blue-50 text-blue-700",
                              },
                              {
                                name: "Risk Analysis",
                                icon: AlertTriangle,
                                color: "bg-red-50 text-red-700",
                              },
                              {
                                name: "Date Extraction",
                                icon: Calendar,
                                color: "bg-green-50 text-green-700",
                              },
                              {
                                name: "Client Brief",
                                icon: Users,
                                color: "bg-purple-50 text-purple-700",
                              },
                            ].map((favorite, idx) => (
                              <motion.div
                                key={idx}
                                whileHover={{ scale: 1.02 }}
                                className={`${favorite.color} p-4 rounded-xl border cursor-pointer`}
                              >
                                <div className="flex flex-col items-center text-center">
                                  <favorite.icon className="w-6 h-6 mb-2" />
                                  <span className="text-sm font-medium">{favorite.name}</span>
                                  <Star className="w-3 h-3 text-yellow-500 fill-current mt-1" />
                                </div>
                              </motion.div>
                            ))}
                          </div>

                          <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                            <div className="flex items-center gap-2 text-sm">
                              <Zap className="w-4 h-4 text-yellow-600" />
                              <span className="text-yellow-800">
                                <strong>85% faster</strong> with favorited prompts
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {index === 7 && (
                      // Advanced Intelligence Mode
                      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                        <div className="space-y-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-full flex items-center justify-center">
                              <Cpu className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-bold text-lg">Advanced Intelligence</h4>
                              <p className="text-sm text-gray-600">Deep analysis mode</p>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div className="bg-gradient-to-r from-violet-50 to-indigo-50 p-4 rounded-lg border border-violet-200">
                              <div className="flex items-center gap-2 mb-3">
                                <Zap className="w-4 h-4 text-violet-600" />
                                <span className="font-medium text-violet-800">
                                  Deep Scan Results
                                </span>
                              </div>

                              <div className="space-y-2 text-sm">
                                <div className="bg-red-50 p-2 rounded border border-red-200">
                                  <strong className="text-red-800">⚠️ Risk Detected:</strong>
                                  <p className="text-red-700">
                                    Unusual arbitration clause limits buyer rights (Section 12.4)
                                  </p>
                                </div>

                                <div className="bg-yellow-50 p-2 rounded border border-yellow-200">
                                  <strong className="text-yellow-800">💡 Pattern Found:</strong>
                                  <p className="text-yellow-700">
                                    Commission split differs from MLS standard (2.7% vs 3%)
                                  </p>
                                </div>

                                <div className="bg-blue-50 p-2 rounded border border-blue-200">
                                  <strong className="text-blue-800">📊 Financial Analysis:</strong>
                                  <p className="text-blue-700">
                                    Property tax estimates 23% above county average
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center justify-center text-sm text-gray-600">
                              <BarChart3 className="w-4 h-4 mr-2" />
                              Analysis completed in 12 seconds
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Designed for Real Estate Professionals */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Designed for Real Estate Professionals
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                SmartRead AI understands the unique needs of agents and brokerages in today's
                competitive market.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-white p-8 rounded-2xl border border-gray-200"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">For Agents</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  SmartRead helps you stay client-ready, cut prep time, and deliver professional
                  summaries that build trust.
                </p>
                <div className="space-y-4">
                  {[
                    "Instant document summaries for client meetings",
                    "Professional AI-generated client communications",
                    "Never miss important deadlines or contingencies",
                    "Custom prompts for your specific market needs",
                    "Mobile access for on-the-go document review",
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white p-8 rounded-2xl border border-gray-200"
              >
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                  <Building2 className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">For Brokerages</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Ensure compliance, centralize knowledge, and empower teams with AI-driven insights
                  that scale.
                </p>
                <div className="space-y-4">
                  {[
                    "Team-wide consistency in document analysis",
                    "Comprehensive audit trails for compliance",
                    "Faster onboarding for new agents",
                    "Centralized AI conversations and insights",
                    "Advanced risk detection and flagging",
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* FAQ Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-lg md:text-xl text-gray-700">
                Get answers to common questions about SmartRead AI document intelligence.
              </p>
            </div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 p-8 rounded-2xl border border-gray-200"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Brain className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-900 mb-3">{faq.q}</h3>
                      <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Enhanced Final CTA */}
      <section className="relative bg-[#2563eb] py-24 overflow-hidden">
        {/* AI Neural Network Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg className="absolute inset-0 w-full h-full opacity-20">
            <defs>
              <radialGradient id="aiGradient" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.1" />
              </radialGradient>
            </defs>
            {[...Array(15)].map((_, i) => (
              <motion.circle
                key={i}
                cx={`${Math.random() * 100}%`}
                cy={`${Math.random() * 100}%`}
                r="3"
                fill="url(#aiGradient)"
                initial={{ opacity: 0 }}
                animate={{
                  opacity: [0, 0.6, 0.2, 0.8, 0],
                  r: [2, 6, 4, 8, 2],
                }}
                transition={{
                  duration: Math.random() * 4 + 3,
                  repeat: Infinity,
                  delay: i * 0.5,
                }}
              />
            ))}
          </svg>
        </div>

        {/* Floating AI Icons */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          {[
            { icon: Brain, x: 10, y: 20 },
            { icon: Sparkles, x: 85, y: 15 },
            { icon: Bot, x: 20, y: 70 },
            { icon: MessageSquare, x: 75, y: 80 },
            { icon: Brain, x: 50, y: 30 },
            { icon: Sparkles, x: 65, y: 60 },
            { icon: Bot, x: 30, y: 45 },
            { icon: MessageSquare, x: 90, y: 50 },
          ].map((item, i) => (
            <motion.div
              key={i}
              className="absolute w-10 h-10"
              style={{
                left: `${item.x}%`,
                top: `${item.y}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.3, 0.8, 0.3],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 4 + i * 0.5,
                repeat: Infinity,
                delay: i * 0.8,
                ease: "easeInOut",
              }}
            >
              <item.icon className="w-full h-full text-white" />
            </motion.div>
          ))}
        </div>

        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-8"
            >
              <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 mb-6">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                >
                  <Brain className="w-5 h-5 text-white" />
                </motion.div>
                <span className="text-white font-medium">AI-Powered Document Intelligence</span>
              </div>
            </motion.div>

            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Book a demo of SmartRead AI.
            </h2>
            <p className="text-lg md:text-xl text-blue-100 mb-12 leading-relaxed">
              Automate contract reading, extract key terms, and save hours on every deal.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center opacity-100">
              <Button
                size="lg"
                className="bg-white text-blue-700 hover:bg-blue-50 hover:text-blue-800 px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] transition-all duration-300 border-2 border-white opacity-100 w-full sm:w-auto"
              >
                <Brain className="mr-3 w-5 h-5" />
                Try SmartRead AI
                <ArrowRight className="ml-3 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
